import { useMutation, useQuery, useQueryClient } from "react-query"
import { api } from "@data-phone/react-resource-server-auth"

export type SmsWebhookDirection = "inbound" | "outbound" | "both"
export type SmsWebhookAuthType = "none" | "bearer" | "basic"

export type SmsWebhookAuth =
  | { type: "none" }
  | { type: "bearer"; token: string }
  | { type: "basic"; username: string; password: string }

export type ConfigureSmsWebhookPayload = {
  label?: string
  did?: string | null
  url: string
  failover_url?: string | null
  direction: SmsWebhookDirection
  payload_version?: 1 | 2
  enabled?: boolean
  auth?: SmsWebhookAuth
  headers?: Record<string, string>
}

export type SmsWebhook = Omit<ConfigureSmsWebhookPayload, "auth"> & {
  id: string
  account_id: string
  auth_type: SmsWebhookAuthType
  created_at?: string
  updated_at?: string
}

type ApiEnvelope<T> = { status: number; data: T }

const webhooksBase = () => `${process.env.REACT_APP_SMS_API_URL}/webhooks`
export const SMS_WEBHOOKS_QUERY_KEY = ["sms", "webhooks"] as const

export const listSmsWebhooks = async (): Promise<SmsWebhook[]> => {
  const response = await api.get<ApiEnvelope<SmsWebhook[]>>(webhooksBase())
  return response.data
}

export const configureSmsWebhook = async (data: ConfigureSmsWebhookPayload): Promise<SmsWebhook> => {
  const response = await api.post<ApiEnvelope<SmsWebhook>>(webhooksBase(), data)
  return response.data
}

export const updateSmsWebhook = async (
  id: string,
  data: Partial<ConfigureSmsWebhookPayload>
): Promise<SmsWebhook> => {
  const response = await api.put<ApiEnvelope<SmsWebhook>>(`${webhooksBase()}/${id}`, data)
  return response.data
}

export const deleteSmsWebhook = async (id: string): Promise<SmsWebhook> => {
  const response = await api.delete<ApiEnvelope<SmsWebhook>>(`${webhooksBase()}/${id}`)
  return response.data
}

export const testSmsWebhook = async (id: string): Promise<unknown> => {
  const response = await api.post<ApiEnvelope<unknown>>(`${webhooksBase()}/${id}/test`)
  return response.data
}

export const useListSmsWebhooks = (enabled = true) => {
  return useQuery(SMS_WEBHOOKS_QUERY_KEY, listSmsWebhooks, {
    enabled,
    onError: (error) => {
      console.error("failed to load webhooks", error)
    },
  })
}

export const useConfigureSmsWebhook = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (webhook: SmsWebhook) => void
  onError?: (error: unknown) => void
} = {}) => {
  const queryClient = useQueryClient()
  return useMutation(configureSmsWebhook, {
    onSuccess: (webhook) => {
      queryClient.invalidateQueries(SMS_WEBHOOKS_QUERY_KEY)
      onSuccess?.(webhook)
    },
    onError,
  })
}

export const useUpdateSmsWebhook = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (webhook: SmsWebhook) => void
  onError?: (error: unknown) => void
} = {}) => {
  const queryClient = useQueryClient()
  return useMutation(
    ({ id, values }: { id: string; values: Partial<ConfigureSmsWebhookPayload> }) =>
      updateSmsWebhook(id, values),
    {
      onSuccess: (webhook) => {
        queryClient.invalidateQueries(SMS_WEBHOOKS_QUERY_KEY)
        onSuccess?.(webhook)
      },
      onError,
    }
  )
}

export const useDeleteSmsWebhook = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void
  onError?: (error: unknown) => void
} = {}) => {
  const queryClient = useQueryClient()
  return useMutation(deleteSmsWebhook, {
    onSuccess: () => {
      queryClient.invalidateQueries(SMS_WEBHOOKS_QUERY_KEY)
      onSuccess?.()
    },
    onError,
  })
}

export const useTestSmsWebhook = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void
  onError?: (error: unknown) => void
} = {}) => {
  return useMutation(testSmsWebhook, {
    onSuccess: () => onSuccess?.(),
    onError,
  })
}
